import { contractAddresses, FIVE_MINUTES, shift4Config } from "@src/config";
import { useSwapContext, usePrivySmartWallet } from "@src/contexts";
import { ProcessingState, RoutePath } from "@src/interfaces";
import { ContractName } from "@src/models";
import { getSwapOrderStatus } from "@src/services";
import { logger, weiToHumanReadable } from "@src/utils";
import { delay } from "@src/utils/delay";
import { ErrorView } from "@src/views/ErrorView";
import { ethers } from "ethers";
import { useCallback, useMemo } from "react";

import { useNavigate } from "react-router-dom";
import { useShift4SDK } from "@src/hooks/useShift4SDK";

interface Props {
  onlyDeposit?: boolean;
  onOrderComplete?: () => void;
  onChainlinkDepositComplete?: () => void;
  amount: string;
}

const getFormConfiguration = ({
  isUsingChainlinkFunction,
  chain,
  amount,
  smartWalletAddress,
  route,
  email,
  token,
  themeMode = "light",
  themeColor = "#836FFF",
}) => {
  logger.log("themeMode", themeMode);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const formConfiguration: any = {
    apiKey: chain.shift4Pk,
    cryptoCurrencyCode: isUsingChainlinkFunction
      ? shift4Config[chain.chainId]!.NATIVE
      : shift4Config[chain.chainId]!.USDC,
    fiatCurrencyCode: "USD",
    fiatAmount: amount,
    lockedAmount: true,
    walletAddress: isUsingChainlinkFunction
      ? contractAddresses[chain?.chainId]?.[ContractName.SwapperRouter]
      : smartWalletAddress,
    showWalletAddressForm: false,
    themeMode,
    widgetWidth: 320,
    themeRadius: 20,
    themeColor,
    paymentMethod: "card",
    externalOrderId: route?.id,
    email,
  };

  if (isUsingChainlinkFunction) {
    if (!route?.gasLimit || !route?.id) {
      throw new Error("Gas limit and route id are required");
    }

    formConfiguration.additionalQuotePayload = {
      contractExecution: {
        contractAddress: contractAddresses[chain.chainId]?.SwapperRouter,
        contractId: "a6f332d6-7b3e-48fb-8051-40bfdd827c7e",
        payload: {
          tokenIn: ethers.constants.AddressZero,
          tokenOut: token?.address,
          receiver: smartWalletAddress,
          metadata: {
            routeHash: route?.id,
            gasLimit: route?.gasLimit,
          },
          swapperCalls: route?.swapperCalls,
        },
      },
    };
  }

  return formConfiguration;
};

export const Tx4Shift = ({
  onlyDeposit = false,
  onOrderComplete,
  onChainlinkDepositComplete,
  amount,
}: Props) => {
  const navigate = useNavigate();
  const { smartWalletAddress, email } = usePrivySmartWallet();
  const {
    route,
    token,
    chain,
    isUsingChainlinkFunction,
    integrationConfig,
    setTransactionState,
    executeSwapTransaction,
    setTokenOutAmount,
    setNewPurchaseCounter,
    fetchAllBalances,
  } = useSwapContext();

  // Define order completion handler
  const handleOrderCompleted = useCallback(async () => {
    if (onlyDeposit) {
      if (onOrderComplete) {
        onOrderComplete();
      }
    } else if (route) {
      if (!isUsingChainlinkFunction) {
        setTransactionState(ProcessingState.DepositSuccess);
        navigate(RoutePath.Processing);
        return;
      }

      if (onChainlinkDepositComplete) {
        onChainlinkDepositComplete();
      }

      const startTime = Date.now();
      let isPolling = true;

      while (isPolling) {
        await delay(5_000);

        try {
          const response = await getSwapOrderStatus({
            routeId: route.id,
            chainId: chain!.chainId,
          });

          logger.log({ routeId: route.id, response });

          if (response.status === "SUCCESS") {
            setTokenOutAmount(
              weiToHumanReadable({
                amount: response.amountOut,
                decimals: token?.decimals || 18,
                precisionFractionalPlaces: 5,
              }),
            );
            setTransactionState(ProcessingState.TransactionSuccess);

            fetchAllBalances();
            isPolling = false;
          }

          if (response.status === "FAIL") {
            setTransactionState(ProcessingState.TransactionError);
            isPolling = false;
          }

          if (Date.now() - startTime > FIVE_MINUTES) {
            setTransactionState(ProcessingState.TransactionError);
            isPolling = false;
          }
        } catch (err) {
          console.error(err);
        }
      }
    }
  }, [
    onlyDeposit,
    onOrderComplete,
    route,
    isUsingChainlinkFunction,
    onChainlinkDepositComplete,
    setTransactionState,
    navigate,
    chain,
    setTokenOutAmount,
    token,
    fetchAllBalances,
  ]);

  // Define order failure handler
  const handleOrderFailed = useCallback(() => {
    setTransactionState(ProcessingState.DepositError);
    navigate(RoutePath.Processing);
  }, [setTransactionState, navigate]);

  // Create form configuration
  const formConfiguration = useMemo(
    () =>
      getFormConfiguration({
        chain,
        route,
        token,
        isUsingChainlinkFunction,
        email,
        amount,
        smartWalletAddress,
        themeMode: integrationConfig.styles?.themeMode,
        themeColor: integrationConfig.styles?.brandTheme?.primaryColor,
      }),
    [
      chain,
      route,
      token,
      isUsingChainlinkFunction,
      email,
      amount,
      smartWalletAddress,
      integrationConfig.styles?.themeMode,
      integrationConfig.styles?.brandTheme?.primaryColor,
    ],
  );

  // Use the hook
  const { containerRef, notLoadedInTime } = useShift4SDK({
    formConfiguration,
    onOrderCompleted: handleOrderCompleted,
    onOrderFailed: handleOrderFailed,
    dependencies: [
      route,
      chain,
      amount,
      smartWalletAddress,
      email,
      isUsingChainlinkFunction,
      onlyDeposit,
      token,
      integrationConfig,
      onChainlinkDepositComplete,
      onOrderComplete,
      navigate,
      setTransactionState,
      executeSwapTransaction,
      setTokenOutAmount,
      setNewPurchaseCounter,
      fetchAllBalances,
    ],
  });

  if (notLoadedInTime) {
    return (
      <ErrorView
        title={"Deposit failed"}
        description={"Loading Shift4 widget failed"}
      />
    );
  }

  return (
    <div
      className={`flex justify-center rounded-[12px] overflow-hidden 
        ${
          integrationConfig.styles?.themeMode === "dark"
            ? "bg-black"
            : "bg-white"
        }`}
    >
      <div ref={containerRef} style={{ width: "320px", height: "480px" }}></div>
    </div>
  );
};
